import { Box, Stack, Typography } from '@mui/material'
import React, { useState, useEffect, useCallback } from 'react'
import { escapeHtml, getSegmentComponentDefinition } from '../../utils'
import PlaceholderText from '../common/PlaceholderText'
import { Info } from '@mui/icons-material'

const renderDefinition = (name) => {
  // Remove repetitive part from the name for better readability
  // Ex: Convert PID-13~1-4 to PID-13-4
  const nameWithoutRepetitive = name.replace(/~\d+-/, '-')
  const definition = getSegmentComponentDefinition(nameWithoutRepetitive)
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gap={1}
      fontSize="medium"
    >
      {name} :{' '}
      <Box component="span" sx={{ fontStyle: 'italic', color: 'grey' }}>
        {definition}
      </Box>{' '}
      :
    </Box>
  )
}

const renderValue = (value, color = 'secondary.main') => {
  return (
    <Typography variant="body2" color={color} lineHeight={2} fontSize="medium">
      {escapeHtml(value)}
    </Typography>
  )
}

/**
 * Main function to tokenize an HL7 message string.
 * @param {string} message - The raw HL7 message string.
 * @returns {object} A structured object representing the tokenized message.
 */
function tokenizeHL7(message) {
  const mshSegment = message.split('\n').find((line) => line.startsWith('MSH'))
  if (!mshSegment) {
    throw new Error('Invalid HL7 message: MSH segment not found.')
  }

  if (mshSegment.length < 8) {
    throw new Error(
      'Invalid MSH segment: It is too short to contain the required delimiters.'
    )
  }

  const delimiters = {
    field: mshSegment[3],
    component: mshSegment[4],
    repetition: mshSegment[5],
    escape: mshSegment[6],
    subComponent: mshSegment[7],
  }

  const parsedData = { delimiters, segments: [] }
  const rawSegments = message.split('\n').filter((line) => line.trim() !== '')

  rawSegments.forEach((segmentStr) => {
    const fields = segmentStr.split(delimiters.field)
    const segmentName = fields[0]
    const fieldStartIndex = segmentName === 'MSH' ? 1 : 0

    const segmentData = { name: segmentName, raw: segmentStr, fields: [] }

    fields.slice(1).forEach((fieldStr, index) => {
      if (segmentName === 'MSH' && index === 0) {
        segmentData.fields.push({
          id: `${segmentName}-1`,
          name: `${segmentName}-1`,
          value: delimiters.field,
          components: [],
        })
        return
      }
      if (segmentName === 'MSH' && index === 1) {
        segmentData.fields.push({
          id: `${segmentName}-2`,
          name: `${segmentName}-2`,
          value: `${delimiters.component}${delimiters.repetition}${delimiters.escape}${delimiters.subComponent}`,
          components: [],
        })
        return
      }

      const fieldId = `${segmentName}-${index + fieldStartIndex + 1}`
      // Split by repetition (~)
      const repetitions = fieldStr.split(delimiters.repetition)
      if (repetitions.length > 1) {
        repetitions.forEach((repStr, repIndex) => {
          // Only add '~' for repetition index in ID if more than one repetition
          const repFieldId =
            repetitions.length > 1 ? `${fieldId}~${repIndex + 1}` : fieldId
          const fieldData = {
            id: repFieldId,
            name: fieldId, // keep the same name for all repetitions
            value: repStr,
            components: [],
          }
          const components = repStr.split(delimiters.component)
          if (
            components.length > 1 ||
            (repStr.includes(delimiters.component) && repStr.trim() !== '')
          ) {
            components.forEach((compStr, compIndex) => {
              const compId = `${repFieldId}-${compIndex + 1}`
              const compData = { id: compId, name: compId, value: compStr }
              const subComps = compStr.split(delimiters.subComponent)
              if (subComps.length > 1) {
                compData.subComponents = subComps.map(
                  (subCompStr, subCompIndex) => ({
                    id: `${compId}-${subCompIndex + 1}`,
                    name: `${compId}-${subCompIndex + 1}`,
                    value: subCompStr,
                  })
                )
              }
              fieldData.components.push(compData)
            })
          }
          segmentData.fields.push(fieldData)
        })
      } else {
        const fieldData = {
          id: fieldId,
          name: fieldId,
          value: fieldStr,
          components: [],
        }
        const components = fieldStr.split(delimiters.component)
        if (
          components.length > 1 ||
          (fieldStr.includes(delimiters.component) && fieldStr.trim() !== '')
        ) {
          components.forEach((compStr, compIndex) => {
            const compId = `${fieldId}-${compIndex + 1}`
            const compData = { id: compId, name: compId, value: compStr }
            const subComps = compStr.split(delimiters.subComponent)
            if (subComps.length > 1) {
              compData.subComponents = subComps.map(
                (subCompStr, subCompIndex) => ({
                  id: `${compId}-${subCompIndex + 1}`,
                  name: `${compId}-${subCompIndex + 1}`,
                  value: subCompStr,
                })
              )
            }
            fieldData.components.push(compData)
          })
        }
        segmentData.fields.push(fieldData)
      }
    })
    parsedData.segments.push(segmentData)
  })
  return parsedData
}

// --- React Components ---

/**
 * Component to display a single SubComponent
 */
const SubComponent = ({ data }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {renderDefinition(data.name)}
      {renderValue(data?.value)}
    </Box>
  )
}

/**
 * Component to display a single Component, which may contain SubComponents
 */
const Component = ({ data }) => {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {renderDefinition(data?.name)}
      {data.subComponents && data.subComponents.length > 0 ? (
        <Box
          sx={(theme) => ({
            paddingLeft: 2,
            borderLeft: '2px solid',
            borderColor: theme.palette.success.light,
          })}
        >
          {data.subComponents.map(
            (sub) => sub.value && <SubComponent key={sub.id} data={sub} />
          )}
        </Box>
      ) : (
        renderValue(data?.value)
      )}
    </Box>
  )
}

/**
 * Component to display a single Field, which may contain Components
 */
const Field = ({ data }) => {
  if (data.value === '' && data.name !== 'MSH-1') return null
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.5 }}>
      {renderDefinition(data?.name)}
      {data.components && data.components.length > 0 ? (
        <Box
          sx={(theme) => ({
            paddingLeft: 2,
            borderLeft: '2px solid',
            borderColor: theme.palette.grey[300],
          })}
        >
          {data.components.map(
            (component) =>
              component.value && (
                <Component key={component.id} data={component} />
              )
          )}
        </Box>
      ) : (
        renderValue(data?.value, 'primary.main')
      )}
    </Box>
  )
}

/**
 * Component to display a single Segment, which contains Fields
 */
const Segment = ({ data }) => {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        flex: 1,
        minWidth: 'max-content',
      })}
    >
      <Typography variant="h5" fontWeight="bold" sx={{ marginBottom: '.5rem' }}>
        {data.name}
      </Typography>
      <div className="pl-4 mt-2 border-l-2" style={{ borderColor: 'indigo' }}>
        {data.fields.map((field) => (
          <Field key={field.id} data={field} />
        ))}
      </div>
    </Box>
  )
}

/**
 * The main display component for the tokenized HL7 data.
 * @param {{ tokenizedData: object, onExplain: function }} props
 */
const Hl7OutputDisplay = ({ tokenizedData }) => {
  if (!tokenizedData || !tokenizedData.segments) {
    return (
      <Box
        sx={(theme) => ({
          padding: theme.spacing(4),
          my: theme.spacing(4),
          textAlign: 'center',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        })}
      >
        <PlaceholderText
          variant="overline"
          sx={{ fontSize: '2rem', color: 'text.primary' }}
        >
          No HL7 message is provided
        </PlaceholderText>
        <Typography variant="body1" color="text.secondary">
          Please provide a valid HL7 message to tokenize and display.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        overflowX: 'auto',
        maxHeight: '500px',
        fontFamily: theme.typography.fontFamilyMonospace,
        fontSize: theme.typography.pxToRem(14),
        display: 'flex',
        gap: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      })}
    >
      {tokenizedData.segments.map((segment) => (
        <Segment key={segment.name} data={segment} />
      ))}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={(theme) => ({
          position: 'absolute',
          bottom: theme.spacing(1),
          right: theme.spacing(2),
        })}
      >
        <Info color='info' />
        <Typography variant="caption" color="info.main">
          Scroll to view more data
        </Typography>
      </Stack>
    </Box>
  )
}

/**
 * Main Application Component
 */
/*
const sampleHl7Message = `MSH|^~\&||IUHP CARDIOLOGY KIC||PGHCC|20250730165124|qq3gMrggPOS6RLSoixsJYog1bYZl4dQi|ORM^O01|Q13562988603T1414070|P|2.4
PID|1||56292646^^^^MRN||Cttest^Lisa||20100101|F|||||^^^stao519@alivecor.com~^NET^X.400
PV1|1||KCAR||||||||||||||||001000650695
ORC|NW|33070150847|||||^^^20250730|||||2325^Ofxptl^Sitapsv^^^^^^^^^^PRN
OBR|1|33070150847||^KARDIAMOBILE ECG^^^Mobile ECG||||||||||||2325^Ofxptl^Sitapsv^^^^^^^^^^PRN|^NET^^2325_PGHCC@alivecor.com
OBX|1|ST|CONNECTIONPERIOD||30`;
*/

export default function TokenizedHl7({ hl7Message }) {
  const [tokenizedData, setTokenizedData] = useState(null)
  const [error, setError] = useState('')

  const handleTokenization = useCallback(() => {
    setError('')
    if (!hl7Message || hl7Message?.trim() === '') {
      setTokenizedData(null)
      return
    }
    try {
      const data = tokenizeHL7(hl7Message)
      setTokenizedData(data)
    } catch (err) {
      setError(`An error occurred during parsing: ${err.message}`)
      setTokenizedData(null)
      console.error('HL7 Parsing Error:', err)
    }
  }, [hl7Message])

  // Tokenize the initial message on first render
  useEffect(() => {
    handleTokenization()
  }, [handleTokenization])

  return (
    <Box
      sx={{
        mt: 4,
      }}
    >
      {error ? (
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      ) : (
        <Hl7OutputDisplay tokenizedData={tokenizedData} />
      )}
    </Box>
  )
}
